import { useMemo } from 'react';
import { addDays, startOfDay } from 'date-fns';
import type { ReceiptBatchListFilters } from '@pflegenavi/service-provider-frontend/api';
import { ReceiptBatchState } from '@pflegenavi/service-provider-shared/api-endpoints';
import type { UrlFilters } from '@pflegenavi/web-components';
import {
  urlDateRange,
  urlString,
  useUrlFilters,
} from '@pflegenavi/web-components';

/** The states with a tab. Change requested and declined are not used yet. */
export const RECEIPT_BATCH_TAB_STATES = [
  ReceiptBatchState.Accepted,
  ReceiptBatchState.InReview,
  ReceiptBatchState.Draft,
] as const;

export type ReceiptBatchTab = (typeof RECEIPT_BATCH_TAB_STATES)[number] | 'all';

// Filters live in the URL so returning from a batch restores the view.
const CODECS = {
  status: urlString('status'),
  receiptDate: urlDateRange('receiptFrom', 'receiptTo'),
  acceptedDate: urlDateRange('acceptedFrom', 'acceptedTo'),
};

export type ReceiptBatchFilterKey = keyof typeof CODECS;

export interface ReceiptBatchFiltersState extends UrlFilters<typeof CODECS> {
  tab: ReceiptBatchTab;
  /** The states of the selected tab, undefined for all. */
  state: ReceiptBatchState[] | undefined;
  /** The URL values in the shape the receipt batch list endpoint expects. */
  filters: ReceiptBatchListFilters;
}

const toTab = (value: string | undefined): ReceiptBatchTab =>
  RECEIPT_BATCH_TAB_STATES.find((state) => state === value) ?? 'all';

// The upper bound is exclusive, the start of the next day, so the last second
// of the day is not cut off.
const toExclusiveEnd = (date: Date | undefined): Date | undefined =>
  date ? startOfDay(addDays(date, 1)) : undefined;

/**
 * The receipt batch table filters and status tab, read from the URL and
 * mapped to the list endpoint params.
 *
 * @example
 * const filtersState = useReceiptBatchFilters();
 * useGetReceiptBatchListPhoenixNoSuspense({
 *   state: filtersState.state,
 *   filters: filtersState.filters,
 *   ...
 * });
 */
export function useReceiptBatchFilters(): ReceiptBatchFiltersState {
  const urlFilters = useUrlFilters(CODECS);
  const { values } = urlFilters;
  const tab = toTab(values.status);

  const state = useMemo(() => (tab === 'all' ? undefined : [tab]), [tab]);

  const filters = useMemo<ReceiptBatchListFilters>(
    () => ({
      receiptDateFrom: values.receiptDate?.from,
      receiptDateTo: toExclusiveEnd(values.receiptDate?.to),
      acceptedDateFrom: values.acceptedDate?.from,
      acceptedDateTo: toExclusiveEnd(values.acceptedDate?.to),
    }),
    [values]
  );

  return useMemo(
    () => ({ ...urlFilters, tab, state, filters }),
    [urlFilters, tab, state, filters]
  );
}
